import produce from 'immer';

import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {PipelineEdgeUpsertById} from 'pipelines/network/PipelineDAO';
import type {PipelineEdge, PipelineStep, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForView} from 'pipelines/PipelineViewUtils';
import {
  getInputStepDataRawDatas,
  getPipelineStepRawDataKind,
  type InputStepDatasRecord,
} from 'pipelines/StepDataUtils';
import type {JSONValue} from 'util/Types';

import {setAtLayoutPath} from './utils/LayoutPathUtils';

export type InputDataName = keyof InputStepDatasRecord<PipelineStepType.LAYOUT, 0>;

const LAYOUT_INPUT_NAME_BY_KIND: Record<PipelineStepDataKind, InputDataName> = {
  [PipelineStepDataKind.DATASET]: 'Input datasets',
  [PipelineStepDataKind.CHART]: 'Input charts',
  [PipelineStepDataKind.FILE]: 'Input files',
};

export function getInputDataNameForView(view: PipelineViewGraphQL): InputDataName {
  // The AnalysisViewSelector remaps CHART_VIEW output references from 'Output chart' to
  // 'Output dataset' (via mapChartViewsToDataFrameViews). Detect this case by checking the
  // source step type so chart views still route to 'Input charts'.
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode?.step.stepType === PipelineStepType.CHART_VIEW) {
    return 'Input charts';
  }
  const outputData = getOutputDataForView(view);
  if (outputData == null) {
    return 'Input datasets';
  }
  return LAYOUT_INPUT_NAME_BY_KIND[getPipelineStepRawDataKind(outputData)];
}

export function getNextInputDataIndex(
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  inputDataName: InputDataName
): number {
  const rawDatasLength = getInputStepDataRawDatas(layoutStep, inputDataName).length;
  // Also check existing edge indices in case they are non-contiguous (e.g. after a view removal).
  // Use whichever is higher to avoid collisions.
  const existingEdgeIndices = (layoutStep.incomingEdges ?? [])
    .filter((edge) => edge.toStepData.name === inputDataName)
    .map((edge) => edge.toStepDataIndex)
    .filter((index): index is number => index != null);
  const maxEdgeIndex = existingEdgeIndices.length > 0 ? Math.max(...existingEdgeIndices) + 1 : 0;
  return Math.max(rawDatasLength, maxEdgeIndex);
}

export function convertEdgeToUpsert(edge: PipelineEdge): PipelineEdgeUpsertById {
  return {
    fromStepId: edge.fromStep.id,
    fromStepDataName: edge.fromStepData.name,
    fromStepDataIndex: edge.fromStepDataIndex,
    toStepDataName: edge.toStepData.name,
    toStepDataIndex: edge.toStepDataIndex,
  };
}

export function buildAddViewEdge(
  view: PipelineViewGraphQL,
  inputDataName: InputDataName,
  inputDataIndex: number
): PipelineEdgeUpsertById | null {
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode?.stepOutputReference == null) {
    return null;
  }

  let fromStepDataName = lastNode.stepOutputReference.outputStepData.name;
  let fromStepDataIndex = lastNode.stepOutputReference.rawDataIndex;

  // The AnalysisViewSelector remaps CHART_VIEW output references from 'Output chart' to
  // 'Output dataset' (via mapChartViewsToDataFrameViews). When routing to 'Input charts',
  // redirect the edge to use 'Output chart' to ensure correct data kind matching.
  if (inputDataName === 'Input charts' && fromStepDataName !== 'Output chart') {
    const chartOutput = lastNode.step.outputStepDatas.find((d) => d.name === 'Output chart');
    if (chartOutput != null) {
      fromStepDataName = 'Output chart';
      fromStepDataIndex = 0;
    }
  }

  return {
    fromStepId: lastNode.step.id,
    fromStepDataName,
    fromStepDataIndex,
    toStepDataName: inputDataName,
    toStepDataIndex: inputDataIndex,
  };
}

/**
 * Ensure all edges for the same `toStepDataName` have concrete numeric indices.
 * The backend rejects a mix of null and non-null `toStepDataIndex` values for the
 * same step data name (see `_validate_and_sort_edge_upserts` in pipeline_step_handler.py).
 * Layout edges should always have concrete indices, but this normalizes defensively
 * in case any existing edges were stored with null indices.
 */
export function normalizeEdgeIndices(edges: Array<PipelineEdgeUpsertById>): Array<PipelineEdgeUpsertById> {
  const grouped = new Map<string, Array<PipelineEdgeUpsertById>>();
  for (const edge of edges) {
    if (!grouped.has(edge.toStepDataName)) {
      grouped.set(edge.toStepDataName, []);
    }
    grouped.get(edge.toStepDataName)!.push(edge);
  }

  return [...grouped.values()].flatMap((group) => {
    if (group.every((e) => e.toStepDataIndex != null)) {
      return group;
    }
    // Preserve existing non-null indices; only assign indices for null edges after the current max.
    const concreteIndices = group.filter((e) => e.toStepDataIndex != null).map((e) => e.toStepDataIndex!);
    let nextIndex = concreteIndices.length > 0 ? Math.max(...concreteIndices) + 1 : 0;
    return group.map((edge) =>
      edge.toStepDataIndex != null ? edge : {...edge, toStepDataIndex: nextIndex++}
    );
  });
}

/**
 * Collect all inputReferences from DATA blocks in the given sections.
 * Works on backend config sections where DATA blocks have `inputReference`.
 */
export function collectInputReferences(
  sections: JSONValue
): Array<{inputDataName: string; inputDataIndex: number}> {
  const refs: Array<{inputDataName: string; inputDataIndex: number}> = [];

  function walk(value: JSONValue): void {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        walk(v);
      });
    } else if (typeof value === 'object' && value != null) {
      const obj = value as Record<string, JSONValue>;
      if (obj.type === 'DATA' && typeof obj.inputReference === 'object' && obj.inputReference != null) {
        const ref = obj.inputReference as Record<string, JSONValue>;
        refs.push({
          inputDataName: ref.inputDataName as string,
          inputDataIndex: ref.inputDataIndex as number,
        });
      }
      Object.values(obj).forEach((v) => {
        walk(v);
      });
    }
  }

  walk(sections);
  return refs;
}

export function replaceBlockInConfig<T extends {sections: JSONValue}>(
  config: T,
  layoutPath: Array<string | number>,
  newBlock: {type: 'DATA'; inputReference: {inputDataName: InputDataName; inputDataIndex: number}}
): T {
  return produce(config, (draft) => {
    setAtLayoutPath(draft, layoutPath, newBlock);
  });
}
